import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { Table } from 'apache-arrow'
import type { BlastSource } from '../../../Model/Project/BlastSource.js'
import { fetchOtuCubeConfig } from '../../../Repository/Visualization/OtuCubes.js'
import { useSpinUnspinnedCount } from '../../Component/useSpinMeta.ts'
import type { Filter } from './OtuDataTableView.logic.js'
import type { OtuTableSource, RowMembers, SampleColumnInput } from './OtuTableSource.js'
import { useGroupSource, type GroupLoad, type GroupSourceChoice } from './GroupSource.js'
import {
  columnReads,
  filterGroups,
  filtersNeedRepresentation,
  groupLabel,
  groupMembers,
  groupReadsReader,
  isReadsColumnField,
  pivotGroupReads,
  readsColumnField,
  representationPending,
  resolveSampleColumns,
  sortGroups,
  sortGroupsBy,
  sortNeedsRepresentation,
  toTableRow,
  withRepresentation,
  type OtuReads,
  type OtuTableRow
} from './OtuCubeTable.logic.js'

export type OtuCubeTableInput = {
  scopeId?: string
  blastType: BlastSource
  /** The rows to show; null loads nothing. */
  source: GroupSourceChoice | null
  filters: Filter[]
  sortField: string
  sortDirection: 'ASC' | 'DESC'
  page: number
  pageSize: number
  /** Whether the representation column is shown, which is what makes a classic page wait for the reads matrix. */
  representationVisible: boolean
  /**
   * The read columns to fill each row's `sampleReads` with, a sample or a group of samples each; `null` while the
   * matrix is off. A column sorts as `readsColumnField(key)`.
   */
  sampleColumns: SampleColumnInput[] | null
}

export type { SampleColumnInput, SampleReadsState } from './OtuTableSource.js'

export type OtuCubeTableState = OtuTableSource & { membersOf: (otuId: string) => RowMembers }

type CubeState =
  | { status: 'idle' | 'loading' | 'error' }
  | { status: 'ready'; key: string; load: GroupLoad<unknown> }

type ReadsState = { status: 'loading' | 'error' } | { status: 'ready'; table: Table }

/** Kind of a reads key: the reads cache keeps the latest key of each. */
const readsKind = (key: string) => key.slice(0, key.indexOf(':'))

/** The cache after a request for [key]: [key] last, plus the most recent key of the other kind. */
const keepReads = <V>(cache: ReadonlyMap<string, V>, key: string, value: V): Map<string, V> => {
  const other = [...cache.keys()].reverse().find((k) => readsKind(k) !== readsKind(key))
  const kept = new Map<string, V>()
  if (other !== undefined) {
    kept.set(other, cache.get(other)!)
  }
  return kept.set(key, value)
}

const NO_READS: ReadonlyMap<string, ReadsState> = new Map()

type FetchedMembers = { of: GroupLoad<unknown> | null; members: ReadonlyMap<string, RowMembers> }

/**
 * The OTU table over a {@link GroupSource}, paged locally after one load per source key.
 *
 * A scope with more rows than the backend's threshold first loads its most abundant ones. The reads per sample load
 * only once needed: for the read columns, or for the representation of a group of several OTUs on the page while
 * that column is shown, or of every group under a representation filter or sort. Relative abundance divides by all
 * of a sample's reads in the scope.
 */
export function useOtuCubeTable(input: OtuCubeTableInput): OtuCubeTableState {
  const { scopeId, blastType, filters, sortField, sortDirection, page, pageSize, sampleColumns } = input
  const source = useGroupSource(input.source, scopeId, blastType)
  const ready = source !== null
  const matrixOn = sampleColumns !== null
  const loadKey = source?.key ?? null
  const kind = source?.kind ?? null
  const [wholeOf, setWholeOf] = useState<ReadonlyMap<string, string>>(new Map())
  const whole = kind !== null && wholeOf.get(kind) === loadKey
  const [cubeState, setCubeState] = useState<CubeState>({ status: 'idle' })
  const [attempt, setAttempt] = useState(0)
  // Read by the load, which must not run again when only the grouping changes.
  const currentSource = useRef(source)
  currentSource.current = source

  useEffect(() => {
    const loading = currentSource.current
    if (!loading || loadKey === null) {
      setCubeState({ status: 'idle' })
      return
    }
    let cancelled = false
    setCubeState({ status: 'loading' })
    const load = async () => {
      const topN = whole ? null : (await fetchOtuCubeConfig()).topNThreshold
      const loaded = await loading.load(topN)
      if (cancelled) {
        return
      }
      setCubeState({ status: 'ready', key: loadKey, load: loaded })
    }
    load().catch((e: unknown) => {
      console.error('OTU table data could not be loaded', e)
      if (!cancelled) {
        setCubeState({ status: 'error' })
      }
    })
    return () => {
      cancelled = true
    }
  }, [loadKey, whole, attempt])

  const loaded = cubeState.status === 'ready' && cubeState.key === loadKey ? cubeState.load : null
  const cube = loaded?.cube ?? null
  const scopeSamples = loaded?.scopeSamples ?? null

  const readsKey = loaded && kind ? `${kind}:${loaded.readsKey}` : null
  // Per key, so a switch of source and back finds its reads again; the loads and their states are evicted together.
  const [readsStates, setReadsStates] = useState(NO_READS)
  const [readsWantedFor, setReadsWantedFor] = useState<string | null>(null)
  const readsWanted = readsKey !== null && readsWantedFor === readsKey
  const currentReads = (readsKey !== null && readsStates.get(readsKey)) || null
  const readsStatus = currentReads?.status ?? 'idle'

  // Loads are not cancelled by a cleanup: one outlives a switch of source and back, and only a scope change discards it.
  const readsLoads = useRef(new Map<string, Promise<Table>>())
  useEffect(() => {
    readsLoads.current = new Map()
    setReadsStates(NO_READS)
    setReadsWantedFor(null)
  }, [scopeId])

  const requestReads = useCallback((): Promise<Table> => {
    if (!loaded || !readsKey) {
      return Promise.reject(new Error('No table to load the reads of'))
    }
    const key = readsKey
    const pending = readsLoads.current.get(key)
    if (pending) {
      readsLoads.current = keepReads(readsLoads.current, key, pending)
      return pending
    }
    const settle = (state: ReadsState) => {
      if (readsLoads.current.get(key) === promise) {
        setReadsStates((all) => new Map(all).set(key, state))
      }
    }
    const promise: Promise<Table> = loaded.loadReads().then(
      (table) => {
        settle({ status: 'ready', table })
        return table
      },
      (e: unknown) => {
        console.error('Reads per sample could not be loaded', e)
        settle({ status: 'error' })
        if (readsLoads.current.get(key) === promise) {
          readsLoads.current.delete(key)
        }
        throw e
      }
    )
    readsLoads.current = keepReads(readsLoads.current, key, promise)
    const kept = readsLoads.current
    setReadsStates((all) => {
      const next = new Map<string, ReadsState>([[key, { status: 'loading' }]])
      for (const [k, state] of all) {
        if (k !== key && kept.has(k)) {
          next.set(k, state)
        }
      }
      return next
    })
    return promise
  }, [readsKey, loaded])
  useEffect(() => {
    if (!ready || !readsWanted || readsStatus !== 'idle') {
      return
    }
    requestReads().catch(() => {})
  }, [ready, readsWanted, readsStatus, requestReads])

  const readsTable = currentReads?.status === 'ready' ? currentReads.table : null

  const baseGroups = useMemo(() => (loaded && source ? source.group(loaded) : null), [loaded, source])
  const indexReads = useCallback((table: Table): OtuReads | null => (loaded ? loaded.indexReads(table) : null), [loaded])
  const readsIndex = useMemo(() => (readsTable ? indexReads(readsTable) : null), [readsTable, indexReads])
  const groups = useMemo(
    () =>
      baseGroups && readsIndex && representationPending(baseGroups) ? withRepresentation(baseGroups, readsIndex, scopeSamples) : baseGroups,
    [baseGroups, readsIndex, scopeSamples]
  )

  const pivot = useMemo(
    () => (matrixOn && baseGroups && readsIndex ? pivotGroupReads(baseGroups, readsIndex) : null),
    [matrixOn, baseGroups, readsIndex]
  )
  const columns = useMemo(
    () => (sampleColumns && readsIndex ? resolveSampleColumns(readsIndex, sampleColumns) : null),
    [sampleColumns, readsIndex]
  )
  const readsSortColumn =
    sampleColumns && isReadsColumnField(sortField) ? sampleColumns.findIndex((c) => readsColumnField(c.key) === sortField) : -1

  const needsAllRepresentation =
    !!groups && representationPending(groups) && (filtersNeedRepresentation(filters) || sortNeedsRepresentation(sortField))
  const needsAllReads = readsSortColumn >= 0 && !pivot

  const waiting = needsAllRepresentation || needsAllReads
  // Apart from the page, so turning one does not filter again or re-judge which columns have data.
  const kept = useMemo(() => (cube && groups && !waiting ? filterGroups(groups, cube, filters) : null), [cube, groups, waiting, filters])
  const filteredRows = useMemo((): Iterable<OtuTableRow> | null => {
    if (!kept || !groups || !cube) {
      return null
    }
    return {
      *[Symbol.iterator]() {
        for (const g of kept) {
          yield toTableRow(groups, cube, g)
        }
      }
    }
  }, [kept, groups, cube])

  const view = useMemo((): { results?: OtuCubeTableState['results']; order?: Int32Array } => {
    if (!ready) {
      return {}
    }
    if (cubeState.status === 'error') {
      return { results: 'ERROR' }
    }
    if (!cube || !groups) {
      return {}
    }
    if (waiting) {
      return readsStatus === 'error' ? { results: 'ERROR' } : {}
    }
    if (!kept) {
      return {}
    }
    const sorted =
      pivot && columns && readsSortColumn >= 0
        ? sortGroupsBy(groups, cube, kept, columnReads(pivot, columns.codes[readsSortColumn]!), sortDirection)
        : sortGroups(groups, cube, kept, sortField, sortDirection)
    const readsOfGroup = pivot && columns ? groupReadsReader(pivot, columns) : null
    const rows = Array.from(sorted.subarray(page * pageSize, (page + 1) * pageSize), (g) => {
      const row = toTableRow(groups, cube, g)
      if (readsOfGroup) {
        row.sampleReads = readsOfGroup(g)
      }
      return row
    })
    return { results: { content: rows, totalSize: sorted.length }, order: sorted }
  }, [
    ready,
    cubeState.status,
    cube,
    groups,
    waiting,
    readsStatus,
    kept,
    pivot,
    columns,
    readsSortColumn,
    sortField,
    sortDirection,
    page,
    pageSize
  ])

  const pageRows = typeof view.results === 'string' ? undefined : view.results
  const pagePending = !!pageRows?.content.some((row) => row.representationPending)
  useEffect(() => {
    if (readsKey && (matrixOn || needsAllRepresentation || (input.representationVisible && pagePending))) {
      setReadsWantedFor(readsKey)
    }
  }, [readsKey, matrixOn, needsAllRepresentation, input.representationVisible, pagePending])

  const sampleIds = useMemo(() => (readsIndex ? [...readsIndex.sampleIds].sort() : null), [readsIndex])

  // Fetched per row as its drawer opens and kept only for the load they were fetched against: a reloaded row may
  // group other OTUs now, so its members are fetched again.
  const fetchMembers = source?.fetchMembers ?? null
  const currentLoad = useRef(loaded)
  currentLoad.current = loaded
  const [fetchedMembers, setFetchedMembers] = useState<FetchedMembers>({ of: null, members: new Map() })
  const membersRequested = useRef<{ of: GroupLoad<unknown> | null; rowIds: Set<string> }>({ of: null, rowIds: new Set() })
  const setMembers = useCallback(
    (of: GroupLoad<unknown>, rowId: string, members: RowMembers) =>
      setFetchedMembers((all) => {
        if (of !== currentLoad.current) {
          return all
        }
        return { of, members: new Map(all.of === of ? all.members : []).set(rowId, members) }
      }),
    []
  )
  const requestMembers = useCallback(
    function request(rowIds: readonly string[]) {
      if (!loaded || !fetchMembers) {
        return
      }
      if (membersRequested.current.of !== loaded) {
        membersRequested.current = { of: loaded, rowIds: new Set() }
      }
      const requested = membersRequested.current.rowIds
      for (const rowId of rowIds) {
        if (requested.has(rowId)) {
          continue
        }
        requested.add(rowId)
        setMembers(loaded, rowId, { status: 'loading' })
        fetchMembers(rowId).then(
          (ids) => setMembers(loaded, rowId, { status: 'ready', ids }),
          (e: unknown) => {
            console.error('Row members could not be loaded', e)
            requested.delete(rowId)
            setMembers(loaded, rowId, { status: 'error', retry: () => request([rowId]) })
          }
        )
      }
    },
    [loaded, fetchMembers, setMembers]
  )

  const groupMembersOf = useMemo(() => {
    if (!baseGroups || !cube || fetchMembers) {
      return null
    }
    let groupOfLabel: Map<string, number> | null = null
    // Stable per group, so a drawer re-rendering does not sort a large group again.
    const members = new Map<number, RowMembers>()
    return (otuId: string): RowMembers => {
      if (!groupOfLabel) {
        groupOfLabel = new Map()
        for (let g = 0; g < baseGroups.size; g++) {
          groupOfLabel.set(groupLabel(baseGroups, cube, g), g)
        }
      }
      const g = groupOfLabel.get(otuId)
      if (g === undefined) {
        return NO_MEMBERS
      }
      let row = members.get(g)
      if (!row) {
        members.set(g, (row = { status: 'ready', ids: groupMembers(baseGroups, cube, g) }))
      }
      return row
    }
  }, [baseGroups, cube, fetchMembers])
  const membersOf = useCallback(
    (otuId: string): RowMembers =>
      fetchMembers
        ? ((fetchedMembers.of === loaded ? fetchedMembers.members.get(otuId) : undefined) ?? LOADING_MEMBERS)
        : (groupMembersOf?.(otuId) ?? NO_MEMBERS),
    [fetchMembers, fetchedMembers, loaded, groupMembersOf]
  )

  // The sources are the scope's, whatever the blast type; keep them while another load runs.
  const [sources, setSources] = useState<{ scopeId: string; sources: BlastSource[] } | null>(null)
  const loadedSources = loaded?.sources ?? null
  useEffect(() => {
    if (scopeId && loadedSources) {
      setSources({ scopeId, sources: loadedSources })
    }
  }, [scopeId, loadedSources])

  const representationVisible = input.representationVisible
  const exportRows = useCallback(async () => {
    if (!view.order || !groups || !cube || !baseGroups) {
      return null
    }
    let rowGroups = groups
    let rowPivot = pivot
    let rowColumns = columns
    const needsRepresentation = representationVisible && representationPending(groups)
    const needsMatrix = matrixOn && !(pivot && columns)
    if (needsRepresentation || needsMatrix) {
      const index = indexReads(await requestReads())
      if (!index) {
        return null
      }
      if (needsRepresentation) {
        rowGroups = withRepresentation(baseGroups, index, scopeSamples)
      }
      if (needsMatrix) {
        rowPivot = pivotGroupReads(baseGroups, index)
        rowColumns = sampleColumns ? resolveSampleColumns(index, sampleColumns) : null
      }
    }
    const readsOfGroup = rowPivot && rowColumns ? groupReadsReader(rowPivot, rowColumns) : null
    const order = view.order
    const rows = Array.from(order, (g) => {
      const row = toTableRow(rowGroups, cube, g)
      if (readsOfGroup) {
        row.sampleReads = readsOfGroup(g)
      }
      return row
    })
    return { rows, sampleTotals: rowColumns?.totals ?? null }
  }, [view.order, groups, baseGroups, cube, scopeSamples, representationVisible, matrixOn, requestReads, indexReads, pivot, columns, sampleColumns])

  const loadAll = useCallback(() => {
    if (kind !== null && loadKey !== null) {
      setWholeOf((all) => new Map(all).set(kind, loadKey))
    }
  }, [kind, loadKey])

  const unclusteredOf = source?.unclustered ?? null
  const unclustered = useSpinUnspinnedCount(scopeId, unclusteredOf?.algorithmType ?? '', unclusteredOf?.atTime ?? '', unclusteredOf !== null)
  const unclusteredCount = unclustered?.unspinnedOtus ?? 0

  const retry = useCallback(() => {
    if (readsStatus === 'error' && readsKey) {
      const key = readsKey
      readsLoads.current.delete(key)
      setReadsStates((all) => {
        const next = new Map(all)
        next.delete(key)
        return next
      })
    }
    if (cubeState.status === 'error') {
      setAttempt((n) => n + 1)
    }
  }, [readsStatus, readsKey, cubeState.status])

  let truncation: OtuCubeTableState['truncation'] = null
  if (loaded && source && loaded.topN !== null) {
    const shown = loaded.size
    // The truncation header decides, then the total, and a full cut without either.
    const cut = loaded.truncated ?? (loaded.total !== null ? loaded.total > shown : shown >= loaded.topN)
    if (cut) {
      truncation = { shown, total: loaded.total, unit: source.unit }
    }
  }

  return {
    results: view.results,
    isLoading: ready && view.results === undefined,
    retry,
    representationLoading: readsStatus !== 'error',
    truncation,
    loadAll,
    sources: sources && sources.scopeId === scopeId ? sources.sources : null,
    membersOf,
    requestMembers: fetchMembers ? requestMembers : undefined,
    exportRows,
    filteredRows,
    groupedOut: !!cube && !!baseGroups && !baseGroups.spin && cube.size > 0 && baseGroups.size === 0,
    resultNote: unclusteredCount > 0 ? `${unclusteredCount.toLocaleString()} OTUs not yet clustered` : null,
    sampleReads: {
      status: readsStatus === 'error' ? 'error' : !matrixOn ? 'idle' : pivot && columns ? 'ready' : 'loading',
      sampleIds,
      totals: columns?.totals ?? null
    }
  }
}

const NO_MEMBERS: RowMembers = { status: 'ready', ids: [] }
const LOADING_MEMBERS: RowMembers = { status: 'loading' }
